import FilledButtonM from "@common/components/button/FilledButtonM"
import TextInputM from "@common/components/input/TextInputM"
import ErrorTextM from "@common/components/text/ErrorTextM"
import LinkTextM from "@common/components/text/LinkTextM"
import TextM from "@common/components/text/TextM"
import { HeaderContainerM } from "@common/header/HeaderContainerM"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import {
    apiIdle,
    apiRequest,
    apiRequestWithPrerequest,
} from "@data/redux/actions/api"
import { authService } from "@data/services/authService"
import yup from "@data/utility/validation"
import { yupResolver } from "@hookform/resolvers/yup"
import * as Device from "expo-device"
import { StatusBar } from "@common/systemBars/StatusBar"
import React, { useContext } from "react"
import { useController, useForm } from "react-hook-form"
import { Image, Keyboard, View } from "react-native"
import { Checkbox, IconButton } from "react-native-paper"
import { connect } from "react-redux"
import logo from "../../assets/logo-blue.png"

const formSchema = yup.object().shape({
    user_profile: yup.string().required(),
    email: yup.string().email().required(),
    password: yup.string().required(),
    newsletter_opt_in: yup.boolean().required(),
})

const userTypesTexts = {
    student: "Je m'inscris pour démarrer mon essai",
    tutor: "Je m'inscris pour devenir tuteur",
}

const userTypesEmailPlaceholders = {
    student: " de l'élève",
    tutor: "",
}

const RegisterNewScreen = (props) => {
    const { navigation } = props

    const resolver = yupResolver(formSchema)
    const dimensions = useContext(DimensionsContext)

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver,
        defaultValues: {
            email: "",
            password: "",
            newsletter_opt_in: false,
            user_profile: "tutor",
            device_name: Device.deviceName ?? "WEB",
        },
    })

    const { field: newsletterOptInField } = useController({
        name: "newsletter_opt_in",
        control,
    })

    const { field: userProfileField } = useController({
        name: "user_profile",
        control,
    })

    const onSubmit = (data) => {
        if (props.loggingIn) {
            return
        }
        Keyboard.dismiss()

        props.apiRequestWithPrerequest(authService.registerV2, {}, data)
    }

    useApiStatus(
        authService.registerV2, // api service
        null, // success message on toast (string or null)
        true, // error message on toast (true) or keep it in redux state (false)
        () => {
            navigation.popToTop()
        } // success callback
    )

    return (
        <View
            style={{
                flex: 1,
                backgroundColor: theme.colors.whiteAlmost,
                justifyContent: "flex-start",
                alignSelf: "center",
                alignItems: "center",
                width: "100%",
            }}
        >
            <HeaderContainerM
                style={{
                    justifyContent: "space-between",
                    paddingRight: 15,
                }}
            >
                <IconButton
                    icon={"arrow-left"}
                    iconColor={theme.colors.primary}
                    containerColor={theme.colors.transparent}
                    size={32}
                    onPress={() => {
                        if (navigation.canGoBack()) navigation.goBack()
                        else navigation.replace("menu")
                    }}
                />
            </HeaderContainerM>

            <View
                style={{
                    paddingHorizontal: 15,
                    paddingTop: 20,
                    width: dimensions?.width,
                    alignItems: "center",
                }}
            >
                <View
                    style={{
                        marginBottom: 30,
                        alignItems: "center",
                        alignSelf: "center",
                    }}
                >
                    <Image
                        style={{
                            width: 150,
                            height: 50,
                        }}
                        resizeMode="contain"
                        source={logo}
                    />
                </View>
                <TextM
                    fontSize={24}
                    fontWeight={"ExtraBold"}
                    color={theme.colors.primary}
                    style={{ textAlign: "center", marginBottom: 20 }}
                    wrap
                >
                    {userTypesTexts[userProfileField.value]}
                </TextM>

                <TextInputM
                    placeholder={`Adresse email${
                        userTypesEmailPlaceholders[userProfileField.value]
                    }`}
                    name="email"
                    textContentType="emailAddress"
                    keyboardType="email-address"
                    liveCheck={{ service: authService.checkEmailAddress }}
                    liveCheckPreRequest={true}
                    control={control}
                    errors={errors}
                />

                <TextInputM
                    placeholder="Mot de passe"
                    name="password"
                    control={control}
                    password
                    errors={errors}
                />

                <Checkbox.Item
                    label="J’accepte de recevoir des actualités et des offres de Masteur"
                    position={"leading"}
                    onPress={() => {
                        newsletterOptInField.onChange(
                            !newsletterOptInField.value
                        )
                    }}
                    color={theme.colors.primary}
                    labelStyle={{
                        fontSize: 14,
                        textAlign: "left",
                    }}
                    style={{
                        marginHorizontal: 5,
                        marginTop: 5,
                    }}
                    status={
                        newsletterOptInField.value ? "checked" : "indeterminate"
                    }
                />

                <ErrorTextM
                    errorData={props.error}
                    style={{ textAlign: "center" }}
                />

                <FilledButtonM
                    style={{ marginTop: 10 }}
                    onPress={handleSubmit(onSubmit)}
                    loading={props.registering}
                    disabled={props.registering}
                    label={"S'inscrire"}
                />
                <View
                    style={{
                        marginBottom: 20,
                        marginTop: 10,
                        paddingHorizontal: 10,
                    }}
                >
                    <TextM
                        style={{
                            fontSize: 12,
                            textAlign: "center",
                        }}
                        wrap
                    >
                        En m'inscrivant, j'accepte les{" "}
                        <LinkTextM url="https://masteur.com/cgv-tuteurs">
                            Conditions générales d'utilisation
                        </LinkTextM>{" "}
                        de Masteur. Je confirme avoir plus de 15 ans. Dans le
                        cas contraire, je confirme avoir l'autorisation d'un
                        parent pour utiliser ce service.
                    </TextM>
                </View>
            </View>
            <View
                style={{
                    marginBottom: 30,
                    marginTop: 30,
                    paddingHorizontal: 10,
                    justifySelf: "flex-end",
                }}
            >
                <TextM style={{ alignSelf: "center" }}>
                    Dèja un compte ?{" "}
                    <LinkTextM
                        onPress={() => {
                            navigation.replace("login")
                        }}
                    >
                        Je me connecte
                    </LinkTextM>
                </TextM>
            </View>
            <StatusBar
                style="dark"
                backgroundColor={theme.colors.white}
                translucent={false}
            />
        </View>
    )
}

const mapStateToProps = (state) => {
    return {
        registered: state.api.registerV2?.success,
        registering: state.api.registerV2?.request,
        error: state.api.registerV2?.error,
        user: state.data.currentUser?.object,
        token: state.data.session?.object?.token,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiRequestWithPrerequest,
    apiIdle,
}

export default connect(mapStateToProps, mapDispatchToProps)(RegisterNewScreen)
